import { useEffect, useRef, useState } from 'react'
import type { ContratoParcela, MedicaoItem } from '@services/db.js'
import type { MedicaoInput } from '@services/gestao.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { formatCurrency } from '@utils/format.js'
import { MEDICAO_ITENS_PADRAO } from './contratosLabels.js'
import {
  ACCEPT_MIDIA_CANTEIRO,
  isVideo,
  textoErroSalvarMidia,
  textoTriagemMidia,
  triagemMidiaCanteiro,
} from '@utils/midia.js'

interface Props {
  open: boolean
  parcela: ContratoParcela | null
  prestador: string
  onClose: () => void
  onLiberar: (id: number, medicao: MedicaoInput) => Promise<void>
}

interface FotoLocal {
  file: File
  url: string
}

const MAX_FOTOS = 6

/**
 * Vistoria antes de liberar a parcela: checklist por marco + fotos como prova.
 * O dono confere no canteiro, item a item, e só então libera o pagamento.
 */
export function MedicaoModal({ open, parcela, prestador, onClose, onLiberar }: Props) {
  const [itens, setItens] = useState<MedicaoItem[]>([])
  const [novoItem, setNovoItem] = useState('')
  const [obs, setObs] = useState('')
  const [fotos, setFotos] = useState<FotoLocal[]>([])
  const [avisoMidia, setAvisoMidia] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [forcar, setForcar] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open || !parcela) return
    setItens(MEDICAO_ITENS_PADRAO[parcela.condicao].map((texto) => ({ texto, ok: false })))
    setNovoItem('')
    setObs('')
    setFotos((prev) => {
      for (const f of prev) URL.revokeObjectURL(f.url)
      return []
    })
    setForcar(false)
    setAvisoMidia('')
  }, [open, parcela])

  if (!parcela) return null

  const conferidos = itens.filter((i) => i.ok).length
  const completo = itens.length > 0 && conferidos === itens.length
  const pendentes = itens.length - conferidos

  function alternar(idx: number) {
    setItens((prev) => prev.map((i, k) => (k === idx ? { ...i, ok: !i.ok } : i)))
  }

  function remover(idx: number) {
    setItens((prev) => prev.filter((_, k) => k !== idx))
  }

  function adicionarItem() {
    const texto = novoItem.trim()
    if (!texto) return
    setItens((prev) => [...prev, { texto, ok: true }])
    setNovoItem('')
  }

  function adicionarArquivos(list: FileList | null) {
    if (!list) return
    const triagem = triagemMidiaCanteiro(Array.from(list), MAX_FOTOS - fotos.length)
    const novos = triagem.aceitos.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }))
    setFotos((prev) => [...prev, ...novos])
    setAvisoMidia(textoTriagemMidia(triagem))
    if (fileRef.current) fileRef.current.value = ''
  }

  function removerFoto(url: string) {
    setFotos((prev) => {
      const alvo = prev.find((f) => f.url === url)
      if (alvo) URL.revokeObjectURL(alvo.url)
      return prev.filter((f) => f.url !== url)
    })
  }

  async function confirmar() {
    if (!parcela) return
    setSalvando(true)
    setAvisoMidia('')
    try {
      await onLiberar(parcela.id!, {
        itens,
        ...(obs.trim() ? { observacoes: obs.trim() } : {}),
        ...(fotos.length > 0 ? { fotos: fotos.map((f) => f.file) } : {}),
      })
      for (const f of fotos) URL.revokeObjectURL(f.url)
      onClose()
    } catch (err) {
      setAvisoMidia(textoErroSalvarMidia(err))
    } finally {
      setSalvando(false)
    }
  }

  const podeLiberar = completo || forcar || itens.length === 0

  return (
    <Modal
      open={open}
      title='Medição do serviço'
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant='primary' onClick={confirmar} loading={salvando} disabled={!podeLiberar}>
            Liberar para pagamento
          </Button>
        </div>
      }
    >
      <div className='cf3-gestao-form'>
        <div className='cf3-block__card cf3-block__card--hint cf3-pagar-resumo'>
          <span className='cf3-pagar-resumo__desc'>{parcela.descricao}</span>
          <span className='cf3-pagar-resumo__meta'>
            {prestador} · {formatCurrency(parcela.valor)}
          </span>
        </div>

        <p className='cf3-gestao-form__nota'>
          Vá ao canteiro e confira item a item. Só libere o que já está feito: depois de pago,
          corrigir custa caro.
        </p>

        <div className='cf-field'>
          <div className='cf3-medicao__head'>
            <span className='cf-field__label'>Checklist da vistoria</span>
            <span className='cf3-medicao__contagem' aria-live='polite'>
              {conferidos}/{itens.length} conferidos
            </span>
          </div>
          <ul className='cf3-medicao' aria-label='Itens da vistoria'>
            {itens.map((item, idx) => (
              <li key={`${idx}-${item.texto}`} className='cf3-medicao__item'>
                <label className='cf3-medicao__check'>
                  <input type='checkbox' checked={item.ok} onChange={() => alternar(idx)} />
                  <span className={item.ok ? 'cf3-medicao__texto--ok' : ''}>{item.texto}</span>
                </label>
                <button
                  type='button'
                  className='cf3-medicao__remover'
                  aria-label={`Remover item: ${item.texto}`}
                  onClick={() => remover(idx)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className='cf3-medicao__novo'>
            <input
              className='cf-input'
              type='text'
              placeholder='Outro item conferido…'
              aria-label='Novo item da vistoria'
              value={novoItem}
              onChange={(e) => setNovoItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  adicionarItem()
                }
              }}
            />
            <Button
              variant='secondary'
              size='sm'
              onClick={adicionarItem}
              disabled={!novoItem.trim()}
            >
              Incluir
            </Button>
          </div>
        </div>

        <div className='cf-field'>
          <span className='cf-field__label'>Foto ou vídeo da vistoria (prova datada)</span>
          <div className='cf3-diario-fotos'>
            {fotos.map((f) => (
              <div key={f.url} className='cf3-diario-foto'>
                {isVideo(f.file) ? (
                  <video src={f.url} muted playsInline preload='metadata' />
                ) : (
                  <img src={f.url} alt='' />
                )}
                <button
                  type='button'
                  className='cf3-diario-foto__remove'
                  aria-label='Remover mídia'
                  onClick={() => removerFoto(f.url)}
                >
                  ×
                </button>
              </div>
            ))}
            {fotos.length < MAX_FOTOS && (
              <label className='cf3-diario-foto cf3-diario-foto--add cf3-diario-foto--add-wide'>
                <NavIcon icon='camera' size={20} />
                <span>{fotos.length === 0 ? 'Foto ou vídeo' : 'Mais'}</span>
                <input
                  ref={fileRef}
                  type='file'
                  accept={ACCEPT_MIDIA_CANTEIRO}
                  multiple
                  aria-label='Adicionar foto ou vídeo da vistoria'
                  onChange={(e) => adicionarArquivos(e.target.files)}
                />
              </label>
            )}
          </div>
          {avisoMidia && (
            <p className='cf3-gestao-aviso' role='status'>
              {avisoMidia}
            </p>
          )}
        </div>

        <Textarea
          label='Observações (opcional)'
          value={obs}
          rows={2}
          placeholder='Ex.: faltou rejunte no banheiro, combinado para amanhã'
          onChange={(e) => setObs(e.target.value)}
        />

        {!completo && itens.length > 0 && (
          <div className='cf3-medicao__pendente'>
            <p className='cf3-gestao-aviso'>
              {pendentes} {pendentes === 1 ? 'item não conferido' : 'itens não conferidos'}. O ideal
              é liberar só com tudo em ordem.
            </p>
            {!forcar && (
              <button
                type='button'
                className='cf3-parcela__btn cf3-parcela__btn--danger'
                onClick={() => setForcar(true)}
              >
                Liberar mesmo assim
              </button>
            )}
          </div>
        )}

        <p className='cf3-gestao-form__nota'>
          Gera uma anotação no Diário de bordo com o checklist e a mídia, vinculada a este contrato.
        </p>
      </div>
    </Modal>
  )
}
